import { Badge } from '@workspace/ui/components/badge'
import { Checkbox } from '@workspace/ui/components/checkbox'
import { FormField } from '@workspace/ui/components/form-field'
import { Switch } from '@workspace/ui/components/switch'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { X } from 'lucide-react'
import { useState } from 'react'
import type { PersonalInfo } from './confirm-form-utils'

export function TagsInput({
  value,
  onChange,
  suggestions,
}: {
  value: string
  onChange: (value: string) => void
  suggestions: string[]
}) {
  const tags = value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  const [inputValue, setInputValue] = useState('')

  const availableSuggestions = suggestions.filter(
    (s) => !tags.includes(s) && s.toLowerCase().includes(inputValue.toLowerCase())
  )

  const updateTags = (newTags: string[]) => {
    onChange(newTags.join(', '))
  }

  const addTag = (tag: string) => {
    if (!tags.includes(tag)) {
      updateTags([...tags, tag])
    }
    setInputValue('')
  }

  const removeTag = (tag: string) => {
    updateTags(tags.filter((t) => t !== tag))
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <TextInput
          inputSize="sm"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && inputValue.trim()) {
              e.preventDefault()
              addTag(inputValue.trim())
            }
          }}
          placeholder="Type to search tags..."
        />
        {inputValue && availableSuggestions.length > 0 && (
          <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-md border border-border bg-input">
            {availableSuggestions.slice(0, 8).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => addTag(s)}
                className="w-full px-3 py-1.5 text-left text-sm text-white hover:bg-hover"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <Badge key={tag} variant="secondary" size="sm" className="gap-1 pr-1">
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="rounded-full p-0.5 hover:bg-white/10"
              >
                <X className="size-2.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

export function CheckboxGroup({
  value,
  onChange,
  options,
}: {
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
}) {
  const selected = value
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)

  const toggle = (label: string) => {
    const updated = selected.includes(label)
      ? selected.filter((s) => s !== label)
      : [...selected, label]
    onChange(updated.join(', '))
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((option) => {
        const isChecked = selected.includes(option.label)
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => toggle(option.label)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
              isChecked
                ? 'border-primary/40 bg-primary/10 text-gray-200'
                : 'border-border bg-input text-muted-foreground hover:border-hover'
            }`}
          >
            <Checkbox
              checked={isChecked}
              onCheckedChange={() => toggle(option.label)}
              className="pointer-events-none"
            />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export function CurrencyInput({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const numericValue = value.replace(/[^0-9]/g, '')
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
        $
      </span>
      <TextInput
        inputSize="sm"
        inputMode="numeric"
        value={numericValue}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        className="pl-6"
        disabled={disabled}
      />
    </div>
  )
}

export function FundraisingToggle({
  value,
  onChange,
}: {
  value: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Switch checked={value} onCheckedChange={onChange} />
      <span className="text-sm text-gray-200">{value ? 'Yes' : 'No'}</span>
    </div>
  )
}

export function PersonalInfoSection({
  data,
  disabled,
  onChange,
}: {
  data: PersonalInfo
  disabled?: boolean
  onChange: (data: PersonalInfo) => void
}) {
  const set = (field: keyof PersonalInfo, value: string) => {
    onChange({ ...data, [field]: value })
  }

  return (
    <div className="p-4">
      <h4 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Personal Info
      </h4>
      <div className="space-y-3">
        <FormField label="Name">
          <TextInput
            inputSize="sm"
            value={data.name}
            onChange={(e) => set('name', e.target.value)}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Bio">
          <Textarea
            textareaSize="sm"
            value={data.bio}
            onChange={(e) => set('bio', e.target.value)}
            rows={3}
            disabled={disabled}
          />
        </FormField>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label="Location">
            <TextInput
              inputSize="sm"
              value={data.location}
              onChange={(e) => set('location', e.target.value)}
              disabled={disabled}
            />
          </FormField>
          <FormField label="Website">
            <TextInput
              inputSize="sm"
              value={data.website}
              onChange={(e) => set('website', e.target.value)}
              disabled={disabled}
            />
          </FormField>
        </div>

        <FormField label="LinkedIn">
          <TextInput
            inputSize="sm"
            value={data.linkedIn}
            onChange={(e) => set('linkedIn', e.target.value)}
            disabled={disabled}
          />
        </FormField>
      </div>
    </div>
  )
}
